import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentRef,
  type ReactNode,
} from "react";
import {
  BottomSheet,
  BottomSheetScrollView,
  BottomSheetTextInput,
  type BottomSheetBackgroundProps,
} from "@-/com.npmjs.gorhom__bottom-sheet";
import {
  View,
  ScrollView,
  Keyboard,
  Platform,
  TextInput,
  type LayoutChangeEvent,
} from "@-/com.npmjs.react-native";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import runes from "@-/com.npmjs.runes2";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import {
  cloneCustomLevel,
  createCustomCard,
  createCustomLevel,
  insertCustomSlide,
  type CustomLevel,
  type CustomLevelItem,
} from "@-/dev.improve.haniverse.lib.level.custom";
import { useBrowseTabsContentBottomInset } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._lib.content-bottom-inset";
import { useCustomLevelsContext } from "#custom-levels-context";
import { getCustomCopySources } from "#custom-levels-copy-sources";
import { exportCustomLevelPack } from "#export-custom-level-pack";

const SheetInput =
  Platform.OS === "web" ? withUniwind(TextInput) : withUniwind(BottomSheetTextInput);
const EditorBottomSheet = withUniwind(BottomSheet);
const SheetScrollView = withUniwind(BottomSheetScrollView);
const SNAP_POINTS = ["34%", "68%"];
const CARD_SEPARATORS = /[\s,，、]+/u;
// String.replaceAll resets lastIndex and does not retain matching state.
// oxlint-disable-next-line no-top/no-top-level-state
const ALL_CARD_SEPARATORS = /[\s,，、]+/gu;
const HAN_MORPHEME_PATTERN = /^\p{Script=Han}+$/u;
const FieldFocusContext = createContext<{
  focusField: (y: number) => void;
  measurePanel: (event: LayoutChangeEvent) => void;
} | null>(null);
function SheetBackground({ style }: BottomSheetBackgroundProps) {
  return (
    <View
      // oxlint-disable-next-line tailwindcss/no-restricted-classes -- Sheets use platform-style 28pt exposed corners.
      className="pointer-events-none rounded-t-[28px] border border-border bg-background shadow-xl"
      style={style}
    />
  );
}
function Field({
  label,
  multiline = false,
  onChange,
  testID,
  value,
}: {
  label: string;
  multiline?: boolean;
  onChange: (text: string) => void;
  testID?: string;
  value: string;
}) {
  const fieldFocus = useContext(FieldFocusContext);
  const fieldY = useRef(0);
  return (
    <View
      className="gap-1.5"
      onLayout={(event) => {
        fieldY.current = event.nativeEvent.layout.y;
      }}
    >
      <Text className="text-sm font-semibold">{label}</Text>
      <SheetInput
        accessibilityLabel={label}
        autoCapitalize="sentences"
        className={`rounded-xs border border-input bg-muted/30 px-3 py-2 text-base leading-6 text-foreground ${multiline ? "min-h-32" : "min-h-12"}`}
        multiline={multiline}
        onChangeText={onChange}
        onFocus={() => fieldFocus?.focusField(fieldY.current)}
        testID={testID}
        textAlignVertical={multiline ? "top" : "center"}
        value={value}
      />
    </View>
  );
}
function Action({
  children,
  disabled = false,
  onPress,
  testID,
}: {
  children: ReactNode;
  disabled?: boolean;
  onPress: () => void;
  testID?: string;
}) {
  const { isSaving } = useCustomLevelsContext();
  return (
    <Button
      disabled={disabled || isSaving}
      onPress={onPress}
      size="sm"
      testID={testID}
      variant="outline"
    >
      <Text>{children}</Text>
    </Button>
  );
}

function CopyLevelsPanel() {
  const editor = useCustomLevelsContext();
  const fieldFocus = useContext(FieldFocusContext);
  const sources = useMemo(() => getCustomCopySources(editor.packs), [editor.packs]);
  const [sourceId, setSourceId] = useState<string>(editor.pack?.script ?? "simplified");
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(40);
  const source = sources.find((entry) => entry.id === sourceId) ?? sources[0];
  const filtered =
    source?.levels.filter((level) =>
      `${level.name} ${level.items.map((item) => (item.kind === "card" ? item.morpheme : item.title)).join(" ")}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    ) ?? [];
  return (
    <View className="gap-3" onLayout={fieldFocus?.measurePanel}>
      <ScrollView
        contentContainerClassName="flex-row gap-2"
        horizontal
        showsHorizontalScrollIndicator={false}
      >
        {sources.map((entry) => (
          <Button
            key={entry.id}
            onPress={() => {
              setSourceId(entry.id);
              setSearch("");
              setCopied(null);
              setVisibleCount(40);
            }}
            size="sm"
            variant={source?.id === entry.id ? "default" : "outline"}
          >
            <Text>{entry.name}</Text>
          </Button>
        ))}
      </ScrollView>
      <Field
        label="Find a level or card"
        onChange={(value) => {
          setSearch(value);
          setVisibleCount(40);
        }}
        value={search}
      />
      {copied ? (
        <Text accessibilityLiveRegion="polite" className="text-sm text-primary">
          Added {copied}. The copy can be edited independently.
        </Text>
      ) : null}
      {filtered.length === 0 ? (
        <Text className="text-muted-foreground">No matching levels.</Text>
      ) : null}
      {filtered.slice(0, visibleCount).map((level) => (
        <Button
          className="h-auto min-h-16 flex-col items-start gap-1 py-3"
          disabled={editor.isSaving}
          key={level.id}
          onPress={() => {
            const copy = cloneCustomLevel(level);
            editor.updateDraft((pack) => ({ ...pack, levels: [...pack.levels, copy] }));
            editor.setSelection({ levelId: copy.id });
            setCopied(level.name);
          }}
          testID={`custom-copy-${level.id}`}
          variant="outline"
        >
          <Text className="font-semibold">{level.name}</Text>
          <Text className="text-xs text-muted-foreground" numberOfLines={1}>
            {level.items
              .map((item) => (item.kind === "card" ? item.morpheme : `▤ ${item.title}`))
              .join(" · ") || "Empty level"}
          </Text>
        </Button>
      ))}
      {filtered.length > visibleCount ? (
        <Action onPress={() => setVisibleCount((count) => count + 40)}>Show more levels</Action>
      ) : null}
    </View>
  );
}

function AddCardsPanel({ level }: { level: CustomLevel | undefined }) {
  const editor = useCustomLevelsContext();
  const fieldFocus = useContext(FieldFocusContext);
  const [text, setText] = useState("");
  const [kind, setKind] = useState<"characters" | "pronunciation" | "words">("characters");
  const [error, setError] = useState<string | null>(null);
  return (
    <View className="gap-3" onLayout={fieldFocus?.measurePanel}>
      <Text className="text-muted-foreground">Adding to {level?.name ?? "a new level"}</Text>
      <View className="flex-row gap-2">
        {(["characters", "pronunciation", "words"] as const).map((mode) => (
          <Button
            key={mode}
            onPress={() => setKind(mode)}
            size="sm"
            variant={kind === mode ? "default" : "outline"}
          >
            <Text>
              {mode === "characters"
                ? "Characters"
                : mode === "pronunciation"
                  ? "Pronunciation"
                  : "Words"}
            </Text>
          </Button>
        ))}
      </View>
      <Field
        label={
          kind !== "words" ? "Characters (e.g. 你好)" : "Words separated by spaces (e.g. 你好 谢谢)"
        }
        multiline
        onChange={setText}
        testID="custom-card-input"
        value={text}
      />
      {error ? (
        <Text accessibilityRole="alert" className="text-destructive">
          {error}
        </Text>
      ) : null}
      <Action
        disabled={!text.trim()}
        onPress={() => {
          const morphemes =
            kind !== "words"
              ? runes(text.replaceAll(ALL_CARD_SEPARATORS, ""))
              : text.trim().split(CARD_SEPARATORS);
          if (
            morphemes.length === 0 ||
            morphemes.some((morpheme) => !HAN_MORPHEME_PATTERN.test(morpheme))
          ) {
            setError("Enter Chinese characters or words.");
            return;
          }
          const target =
            level ?? createCustomLevel(`Level ${(editor.pack?.levels.length ?? 0) + 1}`);
          const cards = morphemes.map((morpheme) =>
            createCustomCard(
              morpheme,
              kind === "words"
                ? "word"
                : kind === "pronunciation"
                  ? "pronunciation"
                  : "meaning-and-pronunciation",
            ),
          );
          editor.updateDraft((pack) => ({
            ...pack,
            levels: level
              ? pack.levels.map((entry) =>
                  entry.id === target.id ? { ...entry, items: [...entry.items, ...cards] } : entry,
                )
              : [...pack.levels, { ...target, items: cards }],
          }));
          editor.setSelection({ itemId: cards[0]?.id, levelId: target.id });
          Keyboard.dismiss();
          editor.openPanel("tools");
        }}
        testID="custom-cards-add"
      >
        Add cards
      </Action>
    </View>
  );
}

export function CustomLevelsEditorSheet() {
  const editor = useCustomLevelsContext();
  const exportMutation = useMutation({
    meta: { deadline: 11 * 60_000 },
    mutationFn: exportCustomLevelPack,
    mutationKey: ["exportCustomLevelPack"],
  });
  const ref = useRef<ComponentRef<typeof BottomSheet>>(null);
  const scrollRef = useRef<ComponentRef<typeof BottomSheetScrollView>>(null);
  const panelY = useRef(0);
  const focusedFieldY = useRef<number | null>(null);
  const scrollFocusedField = useCallback(() => {
    if (Platform.OS !== "web" && focusedFieldY.current !== null) {
      scrollRef.current?.scrollTo({ animated: true, y: Math.max(0, focusedFieldY.current - 8) });
    }
  }, []);
  const focusField = useCallback(
    (y: number) => {
      focusedFieldY.current = panelY.current + y;
      scrollFocusedField();
    },
    [scrollFocusedField],
  );
  const measurePanel = useCallback((event: LayoutChangeEvent) => {
    panelY.current = event.nativeEvent.layout.y;
  }, []);
  const fieldFocus = useMemo(() => ({ focusField, measurePanel }), [focusField, measurePanel]);
  useEffect(() => {
    if (Platform.OS === "web" || !editor.isEditing || !editor.sheetOpen) {
      return;
    }
    // Repeat after the keyboard reduces the sheet's scrollable viewport, when
    // the larger scroll range can reveal the entire focused field.
    const subscription = Keyboard.addListener("keyboardDidShow", scrollFocusedField);
    return () => subscription.remove();
  }, [editor.isEditing, editor.sheetOpen, scrollFocusedField]);
  const bottomInset = useBrowseTabsContentBottomInset();
  const level = editor.pack?.levels.find((entry) => entry.id === editor.selection?.levelId);
  const item = level?.items.find((entry) => entry.id === editor.selection?.itemId);
  const isTall = editor.panel !== "tools";
  useEffect(() => {
    if (editor.sheetOpen) {
      ref.current?.snapToIndex(isTall ? 1 : 0);
    } else {
      ref.current?.close();
    }
  }, [editor.sheetOpen, isTall]);
  const updateLevel = (update: (current: CustomLevel) => CustomLevel) =>
    editor.updateDraft((pack) => ({
      ...pack,
      levels: pack.levels.map((entry) => (entry.id === level?.id ? update(entry) : entry)),
    }));
  const updateItem = (update: (current: CustomLevelItem) => CustomLevelItem) =>
    updateLevel((entry) => ({
      ...entry,
      items: entry.items.map((current) => (current.id === item?.id ? update(current) : current)),
    }));
  const addSlide = (side: "before" | "after") => {
    if (level === undefined || item?.kind !== "card") {
      return;
    }
    const result = insertCustomSlide(level, item.id, side);
    updateLevel(() => result.level);
    editor.setSelection({ itemId: result.slide.id, levelId: level.id });
    editor.openPanel("slide");
  };
  const move = (direction: -1 | 1) => {
    if (item) {
      updateLevel((entry) => {
        const items = [...entry.items];
        const index = items.findIndex((current) => current.id === item.id);
        const target = index + direction;
        if (index !== -1 && target >= 0 && target < items.length) {
          items.splice(index, 1);
          items.splice(target, 0, item);
        }
        return { ...entry, items };
      });
    } else if (level) {
      editor.updateDraft((pack) => {
        const levels = [...pack.levels];
        const index = levels.findIndex((current) => current.id === level.id);
        const target = index + direction;
        if (index !== -1 && target >= 0 && target < levels.length) {
          levels.splice(index, 1);
          levels.splice(target, 0, level);
        }
        return { ...pack, levels };
      });
    }
  };
  const position = item
    ? (level?.items.findIndex((entry) => entry.id === item.id) ?? 0)
    : (editor.pack?.levels.findIndex((entry) => entry.id === level?.id) ?? 0);
  const count = item ? (level?.items.length ?? 0) : (editor.pack?.levels.length ?? 0);
  return (
    <EditorBottomSheet
      accessible={false}
      backgroundComponent={SheetBackground}
      bottomInset={bottomInset}
      containerClassName={
        Platform.OS === "web" ? "pointer-events-none [&>*]:pointer-events-auto" : undefined
      }
      enableBlurKeyboardOnGesture
      enableDynamicSizing={false}
      enablePanDownToClose
      index={editor.sheetOpen ? (isTall ? 1 : 0) : -1}
      keyboardBehavior="extend"
      keyboardBlurBehavior="restore"
      onClose={() => {
        Keyboard.dismiss();
        editor.setSheetOpen(false);
      }}
      ref={ref}
      snapPoints={SNAP_POINTS}
    >
      <FieldFocusContext value={fieldFocus}>
        <SheetScrollView
          contentContainerClassName="gap-4 px-4 pt-1 pb-8"
          keyboardShouldPersistTaps="handled"
          ref={scrollRef}
        >
          <View className="flex-row items-center justify-between gap-2">
            <Text className="text-lg font-bold">
              {editor.panel === "slide"
                ? "Edit slide"
                : editor.panel === "copy"
                  ? "Copy levels"
                  : editor.panel === "cards"
                    ? "Add cards"
                    : editor.panel === "pack"
                      ? "Pack details"
                      : editor.panel === "level"
                        ? "Level details"
                        : item?.kind === "card"
                          ? `Selected: ${item.morpheme}`
                          : "Pack tools"}
            </Text>
            <Action
              onPress={() => {
                Keyboard.dismiss();
                if (editor.panel === "tools") {
                  editor.setSheetOpen(false);
                } else {
                  editor.openPanel("tools");
                }
              }}
            >
              {editor.panel === "tools" ? "Hide" : "Back"}
            </Action>
          </View>
          {editor.isSaving ? <Text className="text-muted-foreground">Saving pack…</Text> : null}
          {editor.saveError ? (
            <Text accessibilityRole="alert" className="text-destructive">
              {editor.saveError}
            </Text>
          ) : null}
          {exportMutation.error ? (
            <Text accessibilityRole="alert" className="text-destructive">
              {exportMutation.error.message}
            </Text>
          ) : null}
          {editor.panel === "tools" ? (
            <View className="gap-3">
              <Text className="text-sm text-muted-foreground">
                Select a card in the live pack to add slides around it.
              </Text>
              <View className="flex-row flex-wrap gap-2">
                {item?.kind === "card" ? (
                  <>
                    <Action onPress={() => addSlide("before")} testID="custom-slide-before">
                      Slide before
                    </Action>
                    <Action onPress={() => addSlide("after")} testID="custom-slide-after">
                      Slide after
                    </Action>
                  </>
                ) : null}
                <Action onPress={() => editor.openPanel("pack")}>Pack details</Action>
                <Action
                  onPress={() => {
                    const next = createCustomLevel(
                      `Level ${(editor.pack?.levels.length ?? 0) + 1}`,
                    );
                    editor.updateDraft((pack) => ({ ...pack, levels: [...pack.levels, next] }));
                    editor.setSelection({ levelId: next.id });
                    editor.openPanel("level");
                  }}
                  testID="custom-level-add"
                >
                  Add level
                </Action>
                <Action onPress={() => editor.openPanel("copy")} testID="custom-level-copy">
                  Copy levels
                </Action>
                <Action onPress={() => editor.openPanel("cards")}>Add cards</Action>
                <Action
                  disabled={exportMutation.isPending}
                  onPress={() => {
                    if (editor.pack) {
                      exportMutation.mutate(editor.pack);
                    }
                  }}
                >
                  Export JSON
                </Action>
                {level ? (
                  <>
                    <Action
                      onPress={() => {
                        editor.setSelection({ levelId: level.id });
                        editor.openPanel("level");
                      }}
                    >
                      Level details
                    </Action>
                    <Action disabled={position <= 0} onPress={() => move(-1)}>
                      Move earlier
                    </Action>
                    <Action disabled={position >= count - 1} onPress={() => move(1)}>
                      Move later
                    </Action>
                    {item ? (
                      <Action
                        onPress={() => {
                          updateLevel((entry) => ({
                            ...entry,
                            items: entry.items.filter((current) => current.id !== item.id),
                          }));
                          editor.setSelection({ levelId: level.id });
                        }}
                      >
                        Remove {item.kind}
                      </Action>
                    ) : null}
                  </>
                ) : null}
              </View>
            </View>
          ) : null}
          {editor.panel === "pack" && editor.pack ? (
            <View
              className="gap-3"
              onLayout={measurePanel}
              pointerEvents={editor.isSaving ? "none" : "auto"}
            >
              <Field
                label="Pack name"
                onChange={(name) => editor.updateDraft((pack) => ({ ...pack, name }))}
                testID="custom-pack-name"
                value={editor.pack.name}
              />
              <Field
                label="Pack description"
                multiline
                onChange={(description) => editor.updateDraft((pack) => ({ ...pack, description }))}
                value={editor.pack.description}
              />
              <View className="flex-row gap-2">
                {(["simplified", "traditional"] as const).map((script) => (
                  <Button
                    disabled={editor.isSaving}
                    key={script}
                    onPress={() => editor.updateDraft((pack) => ({ ...pack, script }))}
                    size="sm"
                    variant={editor.pack?.script === script ? "default" : "outline"}
                  >
                    <Text>
                      {script === "simplified" ? "Simplified glyphs" : "Traditional glyphs"}
                    </Text>
                  </Button>
                ))}
              </View>
            </View>
          ) : null}
          {level && editor.panel === "level" ? (
            <View
              className="gap-3"
              onLayout={measurePanel}
              pointerEvents={editor.isSaving ? "none" : "auto"}
            >
              <Field
                label="Level name"
                onChange={(name) => updateLevel((entry) => ({ ...entry, name }))}
                value={level.name}
              />
              <Field
                label="Level description"
                multiline
                onChange={(description) => updateLevel((entry) => ({ ...entry, description }))}
                value={level.description}
              />
              <View className="flex-row flex-wrap gap-2">
                <Action onPress={() => editor.openPanel("cards")}>Add cards</Action>
                <Action disabled={position <= 0} onPress={() => move(-1)}>
                  Move earlier
                </Action>
                <Action disabled={position >= count - 1} onPress={() => move(1)}>
                  Move later
                </Action>
                <Action
                  onPress={() => {
                    editor.updateDraft((pack) => ({
                      ...pack,
                      levels: pack.levels.filter((entry) => entry.id !== level.id),
                    }));
                    editor.setSelection(null);
                    editor.openPanel("tools");
                  }}
                >
                  Remove level
                </Action>
              </View>
            </View>
          ) : null}
          {editor.panel === "cards" ? <AddCardsPanel level={level} /> : null}
          {editor.panel === "copy" ? <CopyLevelsPanel /> : null}
          {editor.panel === "slide" && item?.kind === "slide" ? (
            <View
              className="gap-3"
              onLayout={measurePanel}
              pointerEvents={editor.isSaving ? "none" : "auto"}
            >
              <Text className="text-sm text-muted-foreground">
                Changes appear immediately in the pack above.
              </Text>
              <Field
                label="Slide title"
                onChange={(title) =>
                  updateItem((entry) => (entry.kind === "slide" ? { ...entry, title } : entry))
                }
                testID="custom-slide-title"
                value={item.title}
              />
              <Field
                label="Slide text"
                multiline
                onChange={(body) =>
                  updateItem((entry) => (entry.kind === "slide" ? { ...entry, body } : entry))
                }
                testID="custom-slide-body"
                value={item.body}
              />
              <View className="flex-row flex-wrap gap-2">
                <Action disabled={position <= 0} onPress={() => move(-1)}>
                  Move earlier
                </Action>
                <Action disabled={position >= count - 1} onPress={() => move(1)}>
                  Move later
                </Action>
                <Action
                  onPress={() => {
                    updateLevel((entry) => ({
                      ...entry,
                      items: entry.items.filter((current) => current.id !== item.id),
                    }));
                    editor.setSelection(level ? { levelId: level.id } : null);
                    editor.openPanel("tools");
                  }}
                >
                  Remove slide
                </Action>
              </View>
            </View>
          ) : null}
        </SheetScrollView>
      </FieldFocusContext>
    </EditorBottomSheet>
  );
}
